/* =============================================
   Collection slider — pattern-slider.js
   Infinite centered card carousel with drag,
   arrows, featured image crossfade, and
   Shopify theme editor support.
   ============================================= */

if (!customElements.get('pattern-slider')) {
  class PatternSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.totalSlides = 0;
      this.isTransitioning = false;
      this.isDragging = false;
      this.startX = 0;
      this.currentTranslate = 0;
      this.prevTranslate = 0;
      this.animationID = null;
      this.resizeTimeout = null;
    }

    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.viewport = this.querySelector('[data-slider-viewport]');
      this.prevBtn = this.querySelector('[data-slider-prev]');
      this.nextBtn = this.querySelector('[data-slider-next]');
      this.cards = Array.from(this.querySelectorAll('[data-card]'));
      this.featuredImages = Array.from(this.querySelectorAll('[data-featured-img]'));

      this.totalSlides = this.cards.length;
      if (!this.track || this.totalSlides === 0) return;

      if (this.totalSlides > 1) this.cloneSlides();
      this.setupEventListeners();
      this.updateSlider(false);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.animationID);
      clearTimeout(this.resizeTimeout);
    }

    get cardGap() {
      const gap = parseFloat(getComputedStyle(this.track).columnGap);
      return Number.isNaN(gap) ? 0 : gap;
    }

    cloneSlides() {
      const originals = this.cards.slice(0, this.totalSlides);

      for (let i = originals.length - 1; i >= 0; i--) {
        this.track.insertBefore(this.cleanClone(originals[i]), this.track.firstChild);
      }
      originals.forEach((card) => {
        this.track.appendChild(this.cleanClone(card));
      });

      this.cards = Array.from(this.querySelectorAll('[data-card]'));
      this.currentIndex = this.totalSlides;
    }

    /* Clones must not carry Shopify editor attributes, or the theme
       editor gets confused about which element is the "real" block */
    cleanClone(card) {
      const clone = card.cloneNode(true);
      clone.removeAttribute('data-shopify-editor-block');
      clone.setAttribute('aria-hidden', 'true');
      return clone;
    }

    setupEventListeners() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

      this.cards.forEach((card) => {
        card.addEventListener('click', () => {
          if (this.isDragging) return;
          const clickedIndex = this.cards.indexOf(card);
          if (clickedIndex !== this.currentIndex) {
            this.currentIndex = clickedIndex;
            this.updateSlider(true);
          }
        });
      });

      this.track.addEventListener('transitionend', (e) => {
        if (e.target === this.track) this.handleTransitionEnd();
      });

      this.viewport.addEventListener('mousedown', this.dragStart.bind(this));
      this.viewport.addEventListener('touchstart', this.dragStart.bind(this), { passive: true });
      this.viewport.addEventListener('mouseup', this.dragEnd.bind(this));
      this.viewport.addEventListener('touchend', this.dragEnd.bind(this));
      this.viewport.addEventListener('mousemove', this.drag.bind(this));
      this.viewport.addEventListener('touchmove', this.drag.bind(this), { passive: true });
      this.viewport.addEventListener('mouseleave', this.dragEnd.bind(this));
      this.viewport.addEventListener('dragstart', (e) => e.preventDefault());

      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => this.updateSlider(false), 100);
      });

      this.bindEditorEvents();
    }

    /* ---------- Drag ---------- */

    dragStart(e) {
      if (this.isTransitioning) return;
      this.isDragging = true;
      this.startX = this.getPositionX(e);
      this.viewport.classList.add('is-dragging');
      this.animationID = requestAnimationFrame(this.animation.bind(this));
    }

    drag(e) {
      if (!this.isDragging) return;
      const currentPosition = this.getPositionX(e);
      this.currentTranslate = this.prevTranslate + currentPosition - this.startX;
    }

    dragEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.viewport.classList.remove('is-dragging');
      cancelAnimationFrame(this.animationID);

      const movedBy = this.currentTranslate - this.prevTranslate;
      const threshold = 50;

      if (movedBy < -threshold) this.next();
      else if (movedBy > threshold) this.prev();
      else this.updateSlider(true);
    }

    getPositionX(e) {
      return e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
    }

    animation() {
      if (!this.isDragging) return;
      const cardWidth = this.cards[0].offsetWidth;
      const viewportWidth = this.viewport.offsetWidth;
      const centerOffset = viewportWidth / 2 - cardWidth / 2;
      const baseOffset = centerOffset - this.currentIndex * (cardWidth + this.cardGap);

      this.track.style.transition = 'none';
      this.track.style.transform = `translateX(${baseOffset + (this.currentTranslate - this.prevTranslate)}px)`;
      this.animationID = requestAnimationFrame(this.animation.bind(this));
    }

    /* ---------- Navigation ---------- */

    prev() {
      if (this.isTransitioning || this.totalSlides <= 1) return;
      this.isTransitioning = true;
      this.currentIndex--;
      this.updateSlider(true);
    }

    next() {
      if (this.isTransitioning || this.totalSlides <= 1) return;
      this.isTransitioning = true;
      this.currentIndex++;
      this.updateSlider(true);
    }

    handleTransitionEnd() {
      if (this.currentIndex >= this.totalSlides * 2) {
        this.currentIndex = this.totalSlides;
        this.updateSlider(false);
      } else if (this.currentIndex < this.totalSlides) {
        this.currentIndex = this.totalSlides * 2 - 1;
        this.updateSlider(false);
      }
      this.isTransitioning = false;
    }

    updateSlider(animate) {
      const cardWidth = this.cards[0].offsetWidth;
      const viewportWidth = this.viewport.offsetWidth;
      const centerOffset = viewportWidth / 2 - cardWidth / 2;
      const offset = centerOffset - this.currentIndex * (cardWidth + this.cardGap);

      this.track.style.transition = animate ? 'transform 0.5s ease' : 'none';
      this.track.style.transform = `translateX(${offset}px)`;
      this.prevTranslate = 0;
      this.currentTranslate = 0;

      const originalIndex = parseInt(this.cards[this.currentIndex].dataset.originalIndex, 10);

      this.cards.forEach((card) => {
        const isActive = parseInt(card.dataset.originalIndex, 10) === originalIndex;
        card.classList.toggle('is-active', isActive);
      });

      this.featuredImages.forEach((img, index) => {
        img.classList.toggle('is-visible', index === originalIndex);
      });
    }

    /* ---------- Shopify theme editor ---------- */

    bindEditorEvents() {
      if (!window.Shopify || !window.Shopify.designMode) return;

      document.addEventListener('shopify:block:select', (e) => {
        const card = e.target.closest ? e.target.closest('[data-card]') : null;
        if (card && this.contains(card) && !card.hasAttribute('aria-hidden')) {
          const index = this.cards.indexOf(card);
          if (index !== -1 && index !== this.currentIndex) {
            this.currentIndex = index;
            this.updateSlider(true);
          }
        }
      });
    }
  }

  customElements.define('pattern-slider', PatternSlider);
}
